'use client';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { type Object3D, PerspectiveCamera, WebGLRenderer } from 'three';
import { degToRad } from '@witmetrics/utils/numbers';
import {
  POSITION_PRESET,
  type PositionPreset,
  TrackballControls,
} from '@/utils/scans/trackballControls';
import { useScene } from '@/providers/SceneProvider';
import { useToggle } from '@/hooks/useToggle';
import { buildGrid } from '@/utils/scans/grid';
import { CAMERA_OFFSET_Z } from '@/utils/scans';
import Tooltip from '@/components/Tooltip';
import IconButton from '@/components/IconButton';
import { HideGridIcon, ShowGridIcon, ThreeDimensionsIcon } from '@/icons';
import Button from '@/components/Button';
import { classes, getHotkey } from './utils';

export type SceneProps = {
  className?: string;
  preserveDrawingBuffer?: boolean;
  onRotate?: (position: PositionPreset) => void;
  onReset?: () => void;
  onMount?: () => void;
};

export default function Scene({
  className,
  preserveDrawingBuffer = false,
  onRotate,
  onReset,
  onMount,
}: SceneProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraRef = useRef<PerspectiveCamera | null>(null);
  const rendererRef = useRef<WebGLRenderer | null>(null);
  const gridRef = useRef<Object3D | null>(null);
  const {
    getScene,
    getControls,
    setControls,
    updateControls,
    resetCamera,
    removeFromScene,
  } = useScene();
  const [isGridShown, toggleGrid] = useToggle();

  useLayoutEffect(() => {
    const width = wrapperRef.current!.clientWidth;
    const height = wrapperRef.current!.clientHeight;
    cameraRef.current = new PerspectiveCamera(10, width / height, 0.1, 1000);
    rendererRef.current = new WebGLRenderer({
      preserveDrawingBuffer,
      canvas: canvasRef.current!,
      antialias: true,
    });
    rendererRef.current.setSize(width, height);
    cameraRef.current.position.z = CAMERA_OFFSET_Z;
    setControls(
      new TrackballControls(cameraRef.current, rendererRef.current.domElement)
    );
    onMount?.();

    const observer = new ResizeObserver(setSizing);
    observer.observe(wrapperRef.current!);
    observer.observe(canvasRef.current!);
    // window.addEventListener('resize', setSizing);
    window.addEventListener('keyup', handleKeyPress);

    let frameId: number;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      updateControls();
      if (gridRef.current && cameraRef.current) {
        const { quaternion, position } = cameraRef.current;
        gridRef.current.quaternion.copy(quaternion);
        gridRef.current.position.set(-position.x, -position.y, -position.z);
        gridRef.current.rotateX(degToRad(90));
      }
      rendererRef.current!.render(getScene(), cameraRef.current!);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
      // window.removeEventListener('resize', setSizing);
      window.removeEventListener('keyup', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    handleToggleGrid();
  }, [isGridShown]);

  const handleKeyPress = (e: KeyboardEvent) => {
    const hotkey = getHotkey(e);
    if (!hotkey) return;
    e.preventDefault();
    e.stopPropagation();
    if (hotkey === 'g') toggleGrid();
    else if (hotkey === 'r') handleReset();
    else onRotate?.(hotkey);
  };

  const handleReset = () => {
    resetCamera();
    onReset?.();
  };

  const setSizing = () => {
    console.log('setting size...');
    const width = wrapperRef.current!.clientWidth;
    const height = wrapperRef.current!.clientHeight;
    cameraRef.current!.aspect = width / height;
    cameraRef.current!.updateProjectionMatrix();
    rendererRef.current!.setSize(width, height);
    // TODO: controls!.handleResize();
  };

  const handleToggleGrid = () => {
    const controls = getControls();
    if (!controls || !controls.object) return;
    if (isGridShown) {
      if (gridRef.current) removeFromScene(gridRef.current.uuid);
      gridRef.current = buildGrid(3000, 750, 0x5f6c75);
      gridRef.current.quaternion.copy(controls.object.quaternion);
      gridRef.current.position.set(0, 0, -CAMERA_OFFSET_Z);
      gridRef.current.rotateX((90 * Math.PI) / 180);
      controls.object.children.push(gridRef.current);
      // addToScene([gridRef.current]);
    } else {
      // removeFromScene(gridRef.current?.uuid || '');
      controls.object.children = controls.object.children.filter(
        (c) => c.uuid !== gridRef.current?.uuid
      );
    }
  };

  return (
    <div ref={wrapperRef} className={twMerge(classes.wrapper, className)}>
      <canvas ref={canvasRef} />
      <div className={classes.controls}>
        {onRotate && (
          <>
            <Tooltip title="Front [1]" placement="top">
              <Button
                className={classes.rotationButton}
                onClick={() => onRotate(POSITION_PRESET.FRONT)}>
                Front
              </Button>
            </Tooltip>
            <Tooltip title="Back [2]" placement="top">
              <Button
                className={classes.rotationButton}
                onClick={() => onRotate(POSITION_PRESET.BACK)}>
                Back
              </Button>
            </Tooltip>
            <Tooltip title="Left [3]" placement="top">
              <Button
                className={classes.rotationButton}
                onClick={() => onRotate(POSITION_PRESET.LEFT)}>
                Left
              </Button>
            </Tooltip>
            <Tooltip title="Right [4]" placement="top">
              <Button
                className={classes.rotationButton}
                onClick={() => onRotate(POSITION_PRESET.RIGHT)}>
                Right
              </Button>
            </Tooltip>
            <Tooltip title="Top [5]" placement="top">
              <Button
                className={classes.rotationButton}
                onClick={() => onRotate(POSITION_PRESET.TOP)}>
                Top
              </Button>
            </Tooltip>
            <Tooltip title="Bottom [6]" placement="top">
              <Button
                className={classes.rotationButton}
                onClick={() => onRotate(POSITION_PRESET.BOTTOM)}>
                Bottom
              </Button>
            </Tooltip>
          </>
        )}
        <Tooltip title="Reset camera [r]" placement="top">
          <IconButton onClick={handleReset}>
            <ThreeDimensionsIcon className={classes.icon} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Toggle grid [g]" placement="top">
          <IconButton onClick={() => toggleGrid()}>
            {isGridShown ? (
              <HideGridIcon className={classes.icon} />
            ) : (
              <ShowGridIcon className={classes.icon} />
            )}
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
